import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Common
    welcome: 'Welcome',
    welcomeBack: 'Welcome back',
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    fullName: 'Full Name',
    role: 'Role',
    submit: 'Submit',
    cancel: 'Cancel',
    saveButton: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search',
    filter: 'Filter',
    close: 'Close',
    continue: 'Continue',
    back: 'Back',
    next: 'Next',
    loading: 'Loading',
    
    // Roles
    patient: 'Patient',
    doctor: 'Doctor',
    nurse: 'Nurse / CHW',
    
    // Navigation
    dashboard: 'Dashboard',
    appointments: 'Appointments',
    payments: 'Payments',
    profile: 'Profile',
    settings: 'Settings',
    overview: 'Overview',
    messages: 'Messages',
    
    // Patient
    bookConsultation: 'Book Consultation',
    maternalHealth: 'Maternal Health',
    childHealth: 'Child Health',
    payPerService: 'Pay-Per-Service',
    activeSubscription: 'Active Subscription',
    nextAppointment: 'Next Appointment',
    healthRecords: 'Health Records',
    totalRecords: 'Total Records',
    lastUpdated: 'Last Updated',
    recentAppointments: 'Recent Appointments',
    quickActions: 'Quick Actions',
    viewAllPatients: 'View All Patients',
    
    // Doctor
    todaysAppointments: "Today's Appointments",
    patients: 'Patients',
    bookings: 'Bookings',
    healthTrackingPage: 'Health Tracking',
    totalPatients: 'Total Patients',
    completedToday: 'Completed Today',
    upcomingToday: 'Upcoming Today',
    
    // Nurse
    uploadVitals: 'Upload Vitals',
    vitalsUploadedToday: 'Vitals Uploaded Today',
    patientsVisited: 'Patients Visited',
    pendingTasks: 'Pending Tasks',
    completedTasks: 'Completed Tasks',
    
    // Services
    teleconsultation: 'Teleconsultation',
    antenatalCare: 'Antenatal Care',
    postnatalCare: 'Postnatal Care',
    immunization: 'Immunization',
    childHealthCheckup: 'Child Health Checkup',
    
    // Landing Page
    heroTitle: 'Quality Maternal & Child Healthcare, From Anywhere',
    heroSubtitle: 'Connect with qualified healthcare professionals for antenatal, postnatal, and child health services',
    trustedMaternalChildHealthcare: 'Trusted Maternal & Child Healthcare',
    healthcareThatBridgesGap: 'Healthcare That Bridges The Gap',
    professionalTelemedicine: 'Professional telemedicine services for mothers and children in Nigeria. Quality care, anytime, anywhere.',
    getStartedFree: 'Get Started Free',
    signIn: 'Sign In',
    patientsServed: 'Patients Served',
    healthcareProviders: 'Healthcare Providers',
    averageRating: 'Average Rating',
    everythingYouNeed: 'Everything You Need',
    comprehensiveMaternal: 'Comprehensive maternal and child healthcare at your fingertips',
    videoConsultations: 'Video Consultations',
    videoConsultationsDesc: 'Connect face-to-face with qualified doctors from the comfort of your home',
    maternalCare: 'Maternal Care',
    maternalCareDesc: 'Specialized antenatal and postnatal care for expecting and new mothers',
    childHealthCare: 'Child Health',
    childHealthDesc: 'Pediatric consultations, vaccination tracking, and growth monitoring',
    availability24_7: '24/7 Availability',
    availability24_7Desc: 'Round-the-clock access to healthcare professionals whenever you need them',
    healthTrackingService: 'Health Tracking',
    healthTrackingDesc: 'Monitor your health journey with comprehensive tracking and insights',
    securePrivate: 'Secure & Private',
    securePrivateDesc: 'HIPAA-compliant platform ensuring your health data stays private and secure',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    features: 'Features',
    pricing: 'Pricing',
    aboutUs: 'About Us',
    contactUs: 'Contact Us',
    
    // How It Works
    howItWorks: 'How It Works',
    getStartedInSteps: 'Get started in 3 simple steps',
    createYourAccount: 'Create Your Account',
    createAccountDesc: 'Sign up in under 2 minutes with your basic information. No paperwork, no hassle.',
    bookAppointment: 'Book Appointment',
    bookAppointmentDesc: 'Choose your preferred service and schedule a consultation at your convenience.',
    getQualityCare: 'Get Quality Care',
    getQualityCareDesc: 'Connect with healthcare professionals and receive personalized care and treatment.',
    
    // Testimonials
    whatPatientsSay: 'What Our Patients Say',
    realStoriesFromRealPeople: 'Real stories from real people',
    testimonial1: 'BridgeCare made my pregnancy journey so much easier. Being able to consult with doctors from home was a blessing!',
    testimonial1Name: 'Aisha M.',
    testimonial1Title: 'New Mother, Lagos',
    testimonial2: 'Quick responses, professional doctors, and affordable prices. BridgeCare is exactly what Nigeria needs!',
    testimonial2Name: 'Chidinma O.',
    testimonial2Title: 'Parent, Abuja',
    testimonial3: 'The health tracking features helped me monitor my baby\'s development. Highly recommend to all parents!',
    testimonial3Name: 'Yusuf A.',
    testimonial3Title: 'Father, Kano',
    
    // CTA
    readyToGetStarted: 'Ready to Get Started?',
    joinThousands: 'Join thousands of families who trust BridgeCare for their healthcare needs',
    createFreeAccount: 'Create Free Account',
    
    // Features (kept for compatibility)
    expertGuidance: 'Expert guidance throughout your pregnancy journey with regular check-ups and monitoring',
    comprehensiveCare: 'Comprehensive care for mother and baby after delivery, ensuring healthy recovery',
    trackAndSchedule: 'Track and schedule your child\'s immunizations with timely reminders',
    signUpStep: 'Sign Up',
    createAccount: 'Create your account in seconds',
    chooseService: 'Choose Service',
    selectFromServices: 'Select from our range of healthcare services',
    connectWithDoctor: 'Connect with Doctor',
    consultQualified: 'Consult with qualified healthcare professionals',
    getCare: 'Get Care',
    receiveQualityHealthcare: 'Receive quality healthcare from anywhere',
    
    // Pricing
    affordablePlans: 'Affordable Plans',
    monthlyPlan: 'Monthly Plan',
    yearlyPlan: 'Yearly Plan',
    perMonth: '/month',
    perYear: '/year',
    savePercent: 'Save',
    unlimitedConsultations: 'Unlimited teleconsultations',
    healthTrackingFeature: 'Pregnancy & child health tracking',
    appointmentReminders: 'Appointment reminders',
    support24_7: '24/7 support',
    allMonthlyFeatures: 'All monthly plan features',
    priorityAccess: 'Priority specialist access',
    freeAssessments: 'Free health assessments',
    discountedTests: 'Discounted lab tests',
    needFrequentConsultations: 'Need Frequent Consultations?',
    saveWithSubscription: 'Save up to 60% with our subscription plans. Unlimited consultations and health tracking included.',
    viewSubscriptionPlans: 'View Subscription Plans',
    
    // Footer
    bridgingGap: 'Bridging the gap in maternal and child healthcare',
    allRightsReserved: 'All rights reserved',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    
    // Auth
    dontHaveAccount: 'Don\'t have an account?',
    alreadyHaveAccount: 'Already have an account?',
    backToHome: 'Back to Home',
    enterYourEmail: 'Enter your email',
    enterYourPassword: 'Enter your password',
    createPassword: 'Create a password',
    enterFullName: 'Enter your full name',
    
    // Dashboard
    hereIsOverview: 'Here\'s your health overview',
    manageRecords: 'Manage your patient records',
    manageAppointments: 'Manage your healthcare appointments',
    viewHistory: 'View your payment history and subscription',
    
    // Appointments
    upcoming: 'Upcoming',
    past: 'Past',
    completed: 'Completed',
    cancelled: 'Cancelled',
    noUpcomingAppointments: 'No upcoming appointments',
    joinCall: 'Join Call',
    videoCall: 'Video Call',
    audioCall: 'Audio Call',
    chatConsultation: 'Chat',
    viewDetails: 'View Details',
    
    // Payments
    subscriptionStatus: 'Subscription Status',
    paymentHistory: 'Payment History',
    noPaymentHistory: 'No payment history',
    subscribeNow: 'Subscribe Now',
    cancelSubscription: 'Cancel Subscription',
    amount: 'Amount',
    status: 'Status',
    description: 'Description',
    date: 'Date',
    
    // Maternal Health
    trackPregnancy: 'Track your pregnancy journey',
    weeksPregnant: 'Weeks Pregnant',
    expectedDueDate: 'Expected Due Date',
    currentPhase: 'Current Phase',
    currentVitals: 'Current Vitals',
    antenatalTimeline: 'Antenatal Care Timeline',
    healthTips: 'Health Tips',
    healthTipsForWeek: 'Health Tips for Week',
    stayHydrated: 'Stay Hydrated',
    drinkWater: 'Drink at least 8-10 glasses of water daily',
    balancedDiet: 'Balanced Diet',
    includeIronFoods: 'Include iron-rich foods and plenty of fruits',
    gentleExercise: 'Gentle Exercise',
    walkingDaily: '20-30 minutes of walking daily is beneficial',
    
    // Child Health
    trackChildGrowth: 'Track your child\'s growth and development',
    immunizationSchedule: 'Immunization Schedule',
    growthChart: 'Growth Chart',
    developmentalMilestones: 'Developmental Milestones',
    age: 'Age',
    gender: 'Gender',
    dateOfBirth: 'Date of Birth',
    phone: 'Phone',
    address: 'Address',
    emergencyContact: 'Emergency Contact',
    bloodGroup: 'Blood Group',
    medicalHistory: 'Medical History',
    selectGender: 'Select Gender',
    selectBloodGroup: 'Select Blood Group',
    female: 'Female',
    male: 'Male',
    enterPhone: 'Enter phone number',
    enterAddress: 'Enter address',
    enterEmergencyContact: 'Enter emergency contact',
    enterMedicalHistory: 'Enter medical history',
    
    // Nurse - Add Patient
    addNewPatient: 'Add New Patient',
    registerPatientToSystem: 'Register a new patient to the system',
    patientAddedSuccessfully: 'Patient added successfully!',
    addPatient: 'Add Patient',
    personalInformation: 'Personal Information',
    contactInformation: 'Contact Information',
    medicalInformation: 'Medical Information',
    hereDashboardOverview: 'Here\'s your dashboard overview',
    nextAppointmentCard: 'Next Appointment',
    subscription: 'Subscription',
    
    // Pay Per Service
    payOnlyForWhatYouNeed: 'Pay only for what you need',
    noSubscriptionRequired: 'No Subscription Required',
    payForIndividualServices: 'Pay only for the services you need, when you need them. No long-term commitment required. For frequent consultations, consider our subscription plans for better value.',
    proceedToPay: 'Proceed to Pay',
    duration: 'Duration',
    
    // Service Checkout
    serviceCheckout: 'Service Checkout',
    completeBooking: 'Complete your booking',
    serviceSummary: 'Service Summary',
    selectDoctor: 'Select Doctor',
    optional: 'Optional',
    available: 'Available',
    unavailable: 'Unavailable',
    paymentMethod: 'Payment Method',
    debitCreditCard: 'Debit/Credit Card',
    bankTransfer: 'Bank Transfer',
    wallet: 'Wallet',
    orderSummary: 'Order Summary',
    serviceFee: 'Service Fee',
    processingFee: 'Processing Fee',
    total: 'Total',
    completePayment: 'Complete Payment',
    processing: 'Processing',
    paymentSuccessful: 'Payment Successful!',
    paymentFailed: 'Payment Failed',
    bookingDetails: 'Booking Details',
    service: 'Service',
    amountPaid: 'Amount Paid',
    bookingId: 'Booking ID',
    confirmationEmail: 'You will receive a confirmation email shortly. Our team will contact you to schedule your appointment.',
    goToDashboard: 'Go to Dashboard',
    bookAnother: 'Book Another Service',
    tryAgain: 'Try Again',
    paymentError: 'We couldn\'t process your payment. Please try again or use a different payment method.',
    backToServices: 'Back to Services',
    
    // Health Tracking
    monitorPatientMetrics: 'Monitor patient health metrics',
    antenatalTracking: 'Antenatal Care Tracking',
    postnatalTracking: 'Postnatal Care Tracking',
    childHealthTracking: 'Child Health Tracking',
    bloodPressure: 'Blood Pressure',
    weight: 'Weight',
    height: 'Height',
    fundalHeight: 'Fundal Height',
    normal: 'Normal',
    
    // Upload Vitals
    recordUploadVitals: 'Record and upload patient vital signs',
    selectPatient: 'Select Patient',
    choosePatient: 'Choose a patient',
    vitalType: 'Vital Type',
    pulseRate: 'Pulse Rate',
    fetalHeartRate: 'Fetal Heart Rate',
    otherVitals: 'Other Vitals',
    temperature: 'Temperature',
    notes: 'Notes',
    optionalNotes: 'Any additional observations or notes',
    vitalsUploaded: 'Vitals Uploaded Successfully',
    vitalsSent: 'The vitals have been recorded and sent to the doctor',
    guidelines: 'Guidelines',
    normalRanges: 'Normal Ranges',
    
    // Login & Signup
    welcomeBackTitle: 'Welcome Back',
    signInToContinue: 'Sign in to continue to BridgeCare',
    emailAddress: 'Email Address',
    enterEmail: 'Enter your email address',
    enterPassword: 'Enter your password',
    selectRole: 'Select your role',
    createAccountTitle: 'Create Account',
    joinBridgeCare: 'Join BridgeCare for quality healthcare',
    signUpButton: 'Sign Up',
    loginButton: 'Login',
    
    // Dashboard Common
    welcomeBackExclaim: 'Welcome Back!',
    doctorDashboard: 'Doctor Dashboard',
    nurseDashboard: 'Nurse/CHW Dashboard',
    welcomeBackDoctor: 'Welcome back, Dr. Amina Yusuf',
    welcomeBackNurse: 'Welcome back',
    welcomeBackNurseCHW: 'Welcome back, Nurse/CHW',
    
    // Additional UI Elements (non-duplicate keys only)
    active: 'Active',
    inactive: 'Inactive',
    renews: 'Renews',
    noAppointments: 'No appointments',
    scheduleOne: 'Schedule one',
    
    // Quick Actions
    bookNow: 'Book Now',
    viewRecords: 'View Records',
    makePayment: 'Make Payment',
    updateProfile: 'Update Profile',
    scheduleAppointment: 'Schedule Appointment',
    viewPatients: 'View Patients',
    uploadVitalsButton: 'Upload Vitals',
    
    // Recent Activity
    recentActivity: 'Recent Activity',
    viewAll: 'View All',
    patientVisited: 'Patient visited',
    taskCompleted: 'Task completed',
    ago: 'ago',
    minutesAgo: 'minutes ago',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',
    
    // Appointments Page
    manageHealthcareAppointments: 'Manage your healthcare appointments',
    appointmentConfirmedSuccessfully: 'Appointment confirmed successfully!',
    appointmentCancelled: 'Appointment cancelled',
    refundProcessedIfApplicable: 'Refund will be processed if applicable',
    confirm: 'Confirm',
    call: 'Call',
    upcomingAppointmentsTitle: 'Upcoming Appointments',
    pastAppointmentsTitle: 'Past Appointments',
    noPastAppointments: 'No past appointments',
    bookYourFirstAppointment: 'Book your first appointment with our healthcare professionals',
    appointmentHistory: 'Your completed and cancelled appointments will appear here',
    doctorsNotes: 'Doctor\'s Notes',
    cancellationReason: 'Cancellation Reason',
    pending: 'Pending',
    
    // Payments Page
    viewPaymentHistory: 'View your payment history and subscription',
    started: 'Started',
    expires: 'Expires',
    on: 'on',
    noActiveSubscription: 'No Active Subscription',
    
    // Health Records Page
    manageMaternalHealth: 'Manage your maternal health and access consultation services',
    currentPregnancyStatus: 'Current Pregnancy Status',
    lastCheckup: 'Last Checkup',
    nextCheckup: 'Next Checkup',
    latestVitals: 'Latest Vitals',
    latestNotes: 'Latest Notes',
    generalConsultation: 'General Consultation',
    prenatalCare: 'Prenatal Care',
    nutritionCounseling: 'Nutrition Counseling',
    mentalHealthSupport: 'Mental Health Support',
    attachments: 'Attachments',
    
    // Maternal Health Page
    trackPregnancyJourney: 'Track your pregnancy journey',
    trimester: 'Trimester',
    firstVisit: 'First Visit',
    firstTrimesterScan: 'First Trimester Scan',
    anatomyScan: 'Anatomy Scan',
    glucoseTest: 'Glucose Test',
    thirdTrimesterVisit: 'Third Trimester Visit',
    weeklyCheckupsBegin: 'Weekly Checkups Begin',
    scheduled: 'Scheduled',
    drinkWaterDaily: 'Drink at least 8-10 glasses of water daily',
    includeIronRichFoods: 'Include iron-rich foods and plenty of fruits',
    
    // Child Health Page
    birth: 'Birth',
    weeks: 'weeks',
    months: 'months',
    
    // Pay Per Service Page
    chooseHealthcareServices: 'Choose and pay for individual healthcare services',
    payOnlyForServicesNeeded: 'Pay only for the services you need, when you need them. No long-term commitment required',
    forFrequentConsultations: 'For frequent consultations, consider our subscription plans for better value',
    oneTimeTeleconsultation: 'One-time Teleconsultation',
    antenatalCareReview: 'Antenatal Care Review',
    postnatalFollowUp: 'Postnatal Follow-up',
    immunizationConsultation: 'Immunization Consultation',
    specialistConsultation: 'Specialist Consultation',
    
    // Service Checkout Page
    appointmentDetails: 'Appointment Details',
    video: 'Video',
    audio: 'Audio',
    chat: 'Chat',
    
    // Appointment Types (NEW - were missing)
    antenatalCheckup: 'Antenatal Check-up',
    postnatalFollowup: 'Postnatal Follow-up',
    pregnancyConsultation: 'Pregnancy Consultation',
    childHealthReview: 'Child Health Review',
    
    // Messages - NEW
    searchPatients: 'Search patients...',
    searchDoctors: 'Search doctors...',
    recentAlerts: 'Recent Alerts',
    
    // Table Headers - NEW
    week: 'Week',
    bp: 'BP',
    weightKg: 'Weight (kg)',
    fundalHeightCm: 'Fundal Height (cm)',
    weeksPostpartum: 'Weeks Postpartum',
    breastfeeding: 'Breastfeeding',
    childParent: 'Child (Parent)',
    
    // Form Labels - NEW
    weightKgLabel: 'Weight (kg)',
    heightCmLabel: 'Height (cm)',
    bloodPressureMmhg: 'Blood Pressure (mmHg)',
    temperatureC: 'Temperature (°C)',
    heartRateBpm: 'Heart Rate (bpm)',
    pregnancyStatus: 'Pregnancy Status',
    gestationalAgeWeeks: 'Gestational Age (weeks)',
    lastMenstrualPeriod: 'Last Menstrual Period',
    
    // UploadVitals - NEW
    recordAndUpload: 'Record and upload patient vital signs',
    selectPatientStar: 'Select Patient *',
    notesOptional: 'Notes (Optional)',
    
    termsAndConditions: 'By completing this payment, you agree to our Terms of Service and Privacy Policy',
    paymentProcessedSuccessfully: 'Your payment has been processed successfully',
    viewAppointments: 'View Appointments',
    couldNotProcessPayment: 'We couldn\'t process your payment. Please try again or use a different payment method',
    selectTime: 'Select Time',
    
    // Nurse Pages
    uploadPatientVitals: 'Upload Patient Vitals',
    recordVitalSigns: 'Record and upload patient vital signs',
    vitalsRecordedAndSent: 'The vitals have been recorded and sent to the doctor',
    bpm: 'bpm',
    anyAdditionalObservations: 'Any additional observations or notes',
    ensureAccurateMeasurements: 'Ensure accurate measurements',
    useCalibratedEquipment: 'Use calibrated equipment',
    recordPatientPosition: 'Record patient position during measurement',
    noteUnusualObservations: 'Note any unusual observations immediately',
    addMedicalRecords: 'Add Medical Records',
    searchForPatient: 'Search for patient',
    searchPatient: 'Search Patient',
    language: 'Language',
    medicalRecordsAddedSuccessfully: 'Medical records added successfully!',
    searchByNameEmailPhone: 'Search by name, email, or phone number',
    hasExistingRecords: 'has existing records',
    noPatientsFound: 'No patients found',
    startTypingToSearch: 'Start typing to search for patients',
    vitalSigns: 'Vital Signs',
    selectStatus: 'Select Status',
    pregnant: 'Pregnant',
    postnatal: 'Postnatal',
    notPregnant: 'Not Pregnant',
    listCurrentMedications: 'List current medications',
    pastMedicalConditions: 'Past medical conditions',
    anyOtherRelevantInfo: 'Any other relevant information',
    pleaseSelectPatientFirst: 'Please select a patient first',
    
    // Messages Page
    searchConversations: 'Search conversations',
    noConversationsYet: 'No conversations yet',
    doctorWillMessageYou: 'Your doctor will message you when they have an update',
    typeAMessage: 'Type a message',
    noConversationSelected: 'No conversation selected',
    selectConversationToStart: 'Select a conversation to start messaging',
    
    // Child Health Page Titles
    trackChildHealthSubtitle: 'Track your child\'s growth and development',
    immunizationScheduleTitle: 'Immunization Schedule',
    growthChartTitle: 'Growth Chart',
    developmentalMilestonesTitle: 'Developmental Milestones',
    healthTipsForAge: 'Health Tips for Age',
    provideBalancedMeals: 'Provide balanced meals with essential nutrients',
    hoursOfSleep: 'Encourage 10-12 hours of sleep daily',
    encouragePlay: 'Encourage play and social learning',
    
    // Doctor Pages
    patientsTitle: 'Patients',
    patientsSubtitle: 'Manage patient records',
    patientList: 'Patient List',
    selectPatientToViewDetails: 'Select a patient to view details',
    healthTrackingTitle: 'Health Tracking',
    healthTrackingSubtitle: 'Monitor patient health metrics',
    antenatalCareTracking: 'Antenatal Care Tracking',
    postnatalCareTracking: 'Postnatal Care Tracking',
    
    // Doctor/Nurse Messages
    newChat: 'New Chat',
    startNewChat: 'Start New Chat',
    clickNewChatToStart: 'Click "New Chat" to start messaging a patient',
    backToPatients: 'Back to Patients',
    
    // Schedule Appointment
    createNewAppointmentFor: 'Create a new appointment for',
    selectDate: 'Select Date',
    selectConsultationType: 'Select Consultation Type',
    appointmentNotes: 'Appointment Notes',
    
    // Nurse Dashboard
    uploadVitalsFor: 'Upload vitals for',
    scheduleHomeVisit: 'Schedule home visit',
    homeVisits: 'Home Visits',
    uploadPatientVitalsTitle: 'Upload Patient Vitals',
    
    // Common Dashboard
    thisWeek: 'This Week',
    newPatients: 'New Patients',
    followUps: 'Follow-ups',
    consultations: 'Consultations',
    vitalsUploadedStat: 'Vitals Uploaded',
    dueToday: 'Due today',
    tomorrow: 'Tomorrow',
    upload: 'Upload',
    schedule: 'Schedule',
    view: 'View',
    selectPatientToStartMessaging: 'Select a patient to start messaging',
    selectDoctorToStartMessaging: 'Select a doctor to start messaging',
    
    // PatientRecord
    allergies: 'Allergies',
    medications: 'Medications',
    vitalsHistory: 'Vitals History',
    consultationHistory: 'Consultation History',
    
    // AddPatient
    searchForPatientAndAdd: 'Search for a patient and add their medical details',
    vitalsUploadedSuccessfully: 'Vitals Uploaded Successfully',
    followUpWith: 'Follow-up with',
    patientUpdates: 'Patient updates',
  },
  yo: {
    // Yoruba translations
    welcome: 'Ẹ káàbọ̀',
    welcomeBack: 'Ẹ káàbọ̀ padà',
    login: 'Wọle',
    signup: 'Forukọsilẹ',
    logout: 'Jade',
    email: 'Imeeli',
    password: 'Ọrọ igbaniwọle',
    name: 'Orukọ',
    fullName: 'Orúkọ ní kíkún',
    role: 'Ipo',
    submit: 'Fi ránṣẹ́',
    cancel: 'Fagilee',
    saveButton: 'Fipamọ',
    delete: 'Paárẹ',
    edit: 'Ṣàtúnṣe',
    search: 'Wá',
    
    // Roles
    patient: 'Alaisàn',
    doctor: 'Dókítà',
    nurse: 'Nọ́ọ̀sì / CHW',
    
    // Navigation
    dashboard: 'Pátákó',
    appointments: 'Àwọn ìpàdé',
    payments: 'Àwọn ìsanwó',
    profile: 'Ìwé-àlàyé',
    settings: 'Ètò',
    messages: 'Àwọn ifiránṣẹ́',
    
    // Services
    bookConsultation: 'Ṣètò Ìpàdé',
    maternalHealth: 'Ìlera Abiyamọ',
    childHealth: 'Ìlera Ọmọdé',
    healthRecords: 'Àwọn ìwé ìlera',
    uploadVitalsButton: 'Gbe àwọn àmì-ayé',
    
    // Dashboard
    welcomeBackExclaim: 'Ẹ káàbọ̀ padà!',
    doctorDashboard: 'Pátákó Dókítà',
    nurseDashboard: 'Pátákó Nọ́ọ̀sì',
    hereDashboardOverview: 'Èyí ni àkópọ̀ ìlera rẹ',
    
    // Stats
    subscriptionStatus: 'Ipò ìforúkọsílẹ̀',
    active: 'Ńṣiṣẹ́',
    inactive: 'Kò ṣiṣẹ́',
    totalPatients: 'Àpapọ̀ àwọn aláisàn',
    totalRecords: 'Àpapọ̀ àwọn ìwé',
    nextAppointment: 'Ìpàdé tókàn',
    
    // Landing/Hero
    heroTitle: 'Ìtọ́jú Ìlera Abiyamọ àti Ọmọdé Tó Dára, Láti Ibikíbi',
    heroSubtitle: 'Pàdé àwọn oníṣègùn tó ní ìmọ̀ fún ìtọ́jú abiyamọ, lẹ́yìn ìbímọ, àti ìlera ọmọdé',
    
    // Login/Signup
    welcomeBackTitle: 'Ẹ káàbọ̀ padà',
    signInToContinue: 'Wọle láti tẹ̀síwájú sí BridgeCare',
    createAccountTitle: 'Ṣẹ̀dá àkáǹtì',
    joinBridgeCare: 'Darapọ̀ mọ́ BridgeCare fún ìtọ́jú ìlera tó dára',
    
    // Common UI (simplified - many terms use English for medical/technical accuracy)
    confirm: 'Jẹ́rìísí',
    pending: 'Nduro',
    completed: 'Parí',
    cancelled: 'Fagilee',
    yes: 'Bẹẹni',
    download: 'Gba kalẹ',
    overview: 'Akopọ',
    tips: 'Imọran',
    viewAll: 'Wo gbogbo',
    
    // Additional keys (using English for medical/technical terms)
    upcomingAppointmentsTitle: 'Àwọn Ìpàdé Tókàn',
    pastAppointmentsTitle: 'Àwọn Ìpàdé Tẹ́lẹ̀',
    noPastAppointments: 'Kò sí ìpàdé tẹ́lẹ̀',
    manageHealthcareAppointments: 'Ṣàkóso àwọn ìpàdé ìtọ́jú ìlera rẹ',
    appointmentConfirmedSuccessfully: 'Ìpàdé ti jẹ́rìísí ṣáájú!',
    refundProcessedIfApplicable: 'A máa ṣe ìdásílẹ̀ owó tó bá yẹ',
    doctorsNotes: 'Àkọsílẹ̀ Dókítà',
    cancellationReason: 'Ìdí Ìfagílé',
    searchConversations: 'Wá ọ̀rọ̀',
    noConversationsYet: 'Kò sí ọ̀rọ̀ síbẹ̀',
    doctorWillMessageYou: 'Dókítà yóò ránṣẹ́ sí ọ nígbà tí wọ́n bá ní ìmúdájú',
    typeAMessage: 'Kọ ìránṣẹ́ kan',
    noConversationSelected: 'Kò sí ọ̀rọ̀ tí a yàn',
    selectConversationToStart: 'Yan ọ̀rọ̀ kan láti bẹ̀rẹ̀ ìránṣẹ́',
    trackChildHealthSubtitle: 'Tọ́jú ìdàgbàsókè àti ìdàgbàsókè ọmọ rẹ',
    immunizationScheduleTitle: 'Àtòjọ Àbẹ̀rẹ́',
    growthChartTitle: 'Àtẹ Ìdàgbàsókè',
    developmentalMilestonesTitle: 'Àwọn Àṣeyọrí Ìdàgbàsókè',
    healthTipsForAge: 'Àwọn Ìmọ̀ràn Ìlera fún Ọjọ́ orí',
    provideBalancedMeals: 'Pèsè oúnjẹ tó ní ìwọ̀ntúnwọ̀nsì pẹ̀lú àwọn èròjà pàtàkì',
    hoursOfSleep: 'A ṣe ìgbàníyànjú oorun wákàtí 10-12 lójoojúmọ́',
    encouragePlay: 'Ìwádìí eré àti ìmọ̀ ìbáṣepọ̀',
    patientsTitle: 'Àwọn Aláisàn',
    patientsSubtitle: 'Ṣàkóso àwọn ìwé ìlera aláisàn',
    patientList: 'Àtòjọ Aláisàn',
    selectPatientToViewDetails: 'Yan aláisàn láti wo àlàyé',
    healthTrackingTitle: 'Ìtọ́pa Ìlera',
    healthTrackingSubtitle: 'Ṣàkóso àwọn ìwọ̀n ìlera aláisàn',
    antenatalCareTracking: 'Ìtọ́pa Ìtọ́jú Aboyún',
    postnatalCareTracking: 'Ìtọ́pa Ìtọ́jú Lẹ́yìn Ìbímọ',
    manageMaternalHealth: 'Ṣàkóso ìlera abiyamọ rẹ àti ìtọ́jú ìmọ̀ràn',
    currentPregnancyStatus: 'Ipò Oyún Lọ́wọ́lọ́wọ́',
    lastCheckup: 'Àyẹ̀wò Tó kẹ́yìn',
    nextCheckup: 'Àyẹ̀wò Tókàn',
    latestVitals: 'Àwọn Àmì Ayé Tuntun',
    latestNotes: 'Àkọsílẹ̀ Tuntun',
    
    // Landing Page
    trustedMaternalChildHealthcare: 'Ìtọ́jú Ìlera Abiyamọ àti Ọmọdé Tó Ní Ìgbẹ́kẹ̀lé',
    healthcareThatBridgesGap: 'Ìtọ́jú Ìlera Tó Ń Kojú Àyé',
    professionalTelemedicine: 'Iṣẹ́ tíìmúlíìsínì ọ̀jọ̀gbọ́n fún àwọn abiyamọ àti ọmọdé ní Nàìjíríà. Ìtọ́jú tó dára, nígbàkúùgbà, níbikíbi.',
    getStartedFree: 'Bẹ̀rẹ̀ Lọ́fẹ̀ẹ́',
    signIn: 'Wọle',
    patientsServed: 'Àwọn Aláisàn Tó Ti Jẹ́ Sìn',
    healthcareProviders: 'Àwọn Oníṣègùn Ìlera',
    averageRating: 'Àpapọ̀ Ìdíwọ̀n',
    everythingYouNeed: 'Gbogbo Ohun Tó Nílò',
    comprehensiveMaternal: 'Ìtọ́jú ìlera abiyamọ àti ọmọdé tó pé ní ìkà rẹ',
    videoConsultations: 'Ìpàdé Fídíò',
    videoConsultationsDesc: 'Sopọ̀ pẹ̀lú àwọn dókítà tó ní ìmọ̀ láti ilé rẹ',
    maternalCare: 'Ìtọ́jú Abiyamọ',
    maternalCareDesc: 'Ìtọ́jú ìṣáájú ìbímọ àti lẹ́yìn ìbímọ fún àwọn abiyamọ',
    childHealthCare: 'Ìtọ́jú Ìlera Ọmọdé',
    childHealthDesc: 'Ìpàdé ọmọdé, ìtọ́pa àbẹ̀rẹ́, àti ìtọ́pa ìdàgbàsókè',
    availability24_7: 'Wíwà 24/7',
    availability24_7Desc: 'Wíwọlé sí àwọn oníṣègùn ìlera nígbàkúùgbà tó bá yẹ',
    healthTrackingService: 'Ìtọ́pa Ìlera',
    healthTrackingDesc: 'Tọ́jú ìrìnàjò ìlera rẹ pẹ̀lú ìtọ́pa àti ìmọ̀ràn',
    securePrivate: 'Àbò àti Ìkọ̀kọ̀',
    securePrivateDesc: 'Pẹpẹ tó ní àbò tó ń dáàbò bo dátà ìlera rẹ',
    howItWorks: 'Báwo Ni Ó Ṣe Ń Ṣiṣẹ́',
    getStartedInSteps: 'Bẹ̀rẹ̀ ní ìgbésẹ̀ mẹ́ta rọrùn',
    createYourAccount: 'Ṣẹ̀dá Àkáǹtì Rẹ',
    createAccountDesc: 'Forúkọsílẹ̀ ní ìṣẹ́jú méjì pẹ̀lú àlàyé ìpìlẹ̀ rẹ. Kò sí ìwé, kò sí wàhálà.',
    bookAppointment: 'Ṣètò Ìpàdé',
    bookAppointmentDesc: 'Yan iṣẹ́ tó wù ọ́ kí o sì ṣètò ìpàdé ní àkókò tó bá wù ọ́.',
    getQualityCare: 'Gba Ìtọ́jú Tó Dára',
    getQualityCareDesc: 'Sopọ̀ pẹ̀lú àwọn oníṣègùn ìlera kí o sì gba ìtọ́jú àti ìtọ́jú tó bára ẹni.',
    whatPatientsSay: 'Ohun Tí Àwọn Aláisàn Sọ',
    realStoriesFromRealPeople: 'Àwọn ìtàn òtítọ́ láti ọ̀dọ̀ àwọn ènìyàn òtítọ́',
    testimonial1: '"BridgeCare jẹ́ kí ìrìnàjò oyún mi rọrùn púpọ̀. Bíbá àwọn dókítà sọ̀rọ̀ láti ilé jẹ́ ìbùkún kan!"',
    testimonial1Name: 'Aisha M.',
    testimonial1Title: 'Ìyá Tuntun, Èkó',
    testimonial2: '"Àwọn ìdáhùn kíákíá, àwọn dókítà ọ̀jọ̀gbọ́n, àti àwọn iye owó tó pọ́n. BridgeCare jẹ́ ohun tó yẹ fún Nàìjíríà!"',
    testimonial2Name: 'Chidinma O.',
    testimonial2Title: 'Òbí, Àbújá',
    testimonial3: '"Àwọn ẹ̀yà ìtọ́pa ìlera ràn mí lọ́wọ́ láti tọ́jú ìdàgbàsókè ọmọ mi. Mo ṣe ìgbàníyànjú rẹ̀ fún gbogbo òbí!"',
    testimonial3Name: 'Yusuf A.',
    testimonial3Title: 'Bàbá, Kano',
    readyToGetStarted: 'Ṣetán Láti Bẹ̀rẹ̀?',
    joinThousands: 'Darapọ̀ mọ́ ẹgbẹẹgbẹ̀rún àwọn ìdílé tó gbẹ́kẹ̀lé BridgeCare fún àwọn nílò ìlera wọn',
    createFreeAccount: 'Ṣẹ̀dá Àkáǹtì Lọ́fẹ̀ẹ́',
    
    // Doctor/Nurse Messages
    newChat: 'Ọ̀rọ̀ Tuntun',
    startNewChat: 'Bẹ̀rẹ̀ Ọ̀rọ̀ Tuntun',
    clickNewChatToStart: 'Tẹ "Ọ̀rọ̀ Tuntun" láti bẹ̀rẹ̀ ìránṣẹ́ aláisàn',
    backToPatients: 'Padà Sí Àwọn Aláisàn',
    
    // Schedule Appointment
    createNewAppointmentFor: 'Ṣẹ̀dá ìpàdé tuntun fún',
    selectDate: 'Yan Ọjọ́',
    selectConsultationType: 'Yan Irú Ìpàdé',
    appointmentNotes: 'Àkọsílẹ̀ Ìpàdé',
    
    // Nurse Dashboard
    uploadVitalsFor: 'Gbe àwọn àmì-ayé fún',
    scheduleHomeVisit: 'Ṣètò ìbẹ̀wò ilé',
    homeVisits: 'Àwọn Ìbẹ̀wò Ilé',
    uploadPatientVitalsTitle: 'Gbe Àwọn Àmì-ayé Aláisàn',
    
    // Common Dashboard
    thisWeek: 'ỌSẹ̀ Yìí',
    newPatients: 'Àwọn Aláisàn Tuntun',
    followUps: 'Àwọn Ìtẹ̀lé',
    consultations: 'Àwọn Ìpàdé',
    vitalsUploadedStat: 'Àmì-ayé Tí Gba',
    dueToday: 'Tí ó yẹ lónìí',
    tomorrow: 'Ọ̀la',
    upload: 'Gbe',
    schedule: 'Ṣètò',
    view: 'Wò',
    selectPatientToStartMessaging: 'Yan aláisàn láti bẹ̀rẹ̀ ìránṣẹ́',
    selectDoctorToStartMessaging: 'Yan dókítà láti bẹ̀rẹ̀ ìránṣẹ́',
    
    // PatientRecord
    allergies: 'Àwọn Àrùn',
    medications: 'Àwọn Oògùn',
    vitalsHistory: 'Ìtàn Àmì-ayé',
    consultationHistory: 'Ìtàn Ìpàdé',
    
    // AddPatient
    searchForPatientAndAdd: 'Wá aláisàn kí o sì fi àwọn àlàyé ìléra wọn',
    vitalsUploadedSuccessfully: 'Àmì-ayé Ti Gba Ṣáájú',
    followUpWith: 'Ìtẹ̀lé pẹ̀lú',
    patientUpdates: 'Àwọn ìmúdájú aláisàn',
    
    // Sidebar
    language: 'Èdè',
    
    // AddPatient Page
    addMedicalRecords: 'Ṣe Àkọsílẹ̀ Ìléra',
    searchPatient: 'Wá Aláisàn',
    searchByNameEmailPhone: 'Wá nípasẹ̀ orúkọ, ìméèlì, tàbí fóònù...',
    medicalRecordsAddedSuccessfully: 'Àkọsílẹ̀ ìléra ti ṣàfikún ṣáájú!',
    
    // UploadVitals Page
    bloodPressure: 'Ìtẹ̀ Ẹ̀jẹ̀',
    systolic: 'Systolic',
    diastolic: 'Diastolic',
    pulseRate: 'Iyára Lìlú',
    guidelines: 'Àwọn Ìtọ́sọ́ná',
    normalRanges: 'Àwọn Iwọ̀n Deede',
    
    // Additional Doctor/Nurse Dashboard Keys
    todaysAppointments: 'Àwọn Ìpàdé Òní',
    welcomeBackDoctor: 'Ẹ káàbọ̀ padà, Dókítà Amina Yusuf',
    welcomeBackNurse: 'Ẹ káàbọ̀ padà',
    recentAlerts: 'Àwọn Ìkìlọ̀ Tuntun',
    searchPatients: 'Wá àwọn aláisàn...',
    searchDoctors: 'Wá àwọn dókítà...',
    
    // Appointment Types
    antenatalCheckup: 'Àyẹ̀wò Aboyún',
    postnatalFollowup: 'Ìtẹ̀lé Lẹ́yìn Ìbímọ',
    pregnancyConsultation: 'Ìgbìmọ̀ Oyún',
    childHealthReview: 'Àtúnyẹ̀wò Ìlera Ọmọdé',
    
    // Status & Mode (only new ones)
    recovering: 'Ńsàn',
    healthy: 'Ìlera Dáradára',
    
    // Table Headers (only new ones)
    week: 'Ọ̀sẹ̀',
    bp: 'BP',
    weightKg: 'Ìwúwo (kg)',
    fundalHeightCm: 'Gíga Fundal (cm)',
    weeksPostpartum: 'Àwọn Ọ̀sẹ̀ Lẹ́yìn Ìbímọ',
    breastfeeding: 'Ìfunmúyàn',
    childParent: 'Ọmọdé (Òbí)',
    
    // Form Labels (only new ones)
    weightKgLabel: 'Ìwúwo (kg)',
    heightCmLabel: 'Gíga (cm)',
    bloodPressureMmhg: 'Ìtẹ̀ Ẹ̀jẹ̀ (mmHg)',
    temperatureC: 'Ìgbónágbooru (°C)',
    heartRateBpm: 'Lílú Ọkàn (bpm)',
    pregnancyStatus: 'Ipò Oyún',
    gestationalAgeWeeks: 'Ọjọ́ orí Oyún (ọ̀sẹ̀)',
    lastMenstrualPeriod: 'Àkókò Nṣe Tó kẹ́yìn',
    
    // UploadVitals (only new ones)
    recordAndUpload: 'Kọ àti gbe àwọn àmì-ayé aláisàn',
    selectPatientStar: 'Yan Aláisàn *',
    notesOptional: 'Àwọn Àkọsílẹ̀ (Àyàn)',
    ensureAccurateMeasurements: 'Rii dájú pé gbogbo wíwọn jẹ́ tó peye',
    useCalibratedEquipment: 'Lo ohun èlò tó ṣe ìṣètò',
    recordPatientPosition: 'Kọ ipò aláisàn nígbà wíwọn',
    noteUnusualObservations: 'Ṣàkíyèsí ohunkóhun tó yàtọ̀ lẹ́sẹ̀kẹsẹ̀',
    greatWork: 'Iṣẹ́ Nlá!',
  },
  ig: {
    // Igbo translations
    welcome: 'Nnọọ',
    welcomeBack: 'Nnọọ ọzọ',
    login: 'Banye',
    signup: 'Debanye aha',
    logout: 'Pụọ',
    email: 'Ozi-eletrọniki',
    password: 'Okwuntughe',
    name: 'Aha',
    fullName: 'Aha zuru ezu',
    role: 'Ọrụ',
    submit: 'Nyefee',
    cancel: 'Kagbuo',
    saveButton: 'Chekwaa',
    delete: 'Hichapụ',
    edit: 'Dezie',
    search: 'Chọọ',
    
    // Roles
    patient: 'Onye ọrịa',
    doctor: 'Dọkịta',
    nurse: 'Nọọsụ / CHW',
    
    // Navigation
    dashboard: 'Dashibọọdụ',
    appointments: 'Nzukọ',
    payments: 'Ịkwụ ụgwọ',
    profile: 'Profaịlụ',
    settings: 'Ntọala',
    messages: 'Ozi',
    
    // Services
    bookConsultation: 'Kpọọ Nzukọ',
    maternalHealth: 'Ahụ Ike Nne',
    childHealth: 'Ahụ Ike Nwa',
    healthRecords: 'Ndekọ ahụ ike',
    uploadVitalsButton: 'Bulite ihe dị mkpa',
    
    // Dashboard
    welcomeBackExclaim: 'Nnọọ ọzọ!',
    doctorDashboard: 'Dashibọọdụ Dọkịta',
    nurseDashboard: 'Dashibọọdụ Nọọsụ',
    hereDashboardOverview: 'Nke a bụ nchịkọta ahụ ike gị',
    
    // Stats
    subscriptionStatus: 'Ọnọdụ ndebanye aha',
    active: 'Na-arụ ọrụ',
    inactive: 'Adịghị arụ ọrụ',
    totalPatients: 'Ngụkọta ndị ọrịa',
    totalRecords: 'Ngụkọta ndekọ',
    nextAppointment: 'Nzukọ na-esote',
    
    // Hero/Landing
    heroTitle: 'Nlekọta Ahụ Ike Nne na Nwa Dị Mma, Site Ebe Ọbụla',
    heroSubtitle: 'Soro ndị ọkachamara ahụ ike kparịta ụka maka nlekọta nne dị ime, mgbe amụrụ nwa, na ahụ ike ụmụaka',
    
    // Login/Signup
    welcomeBackTitle: 'Nnọọ ọzọ',
    signInToContinue: 'Banye ka ị gaa n\'ihu na BridgeCare',
    createAccountTitle: 'Mepụta Akaụntụ',
    joinBridgeCare: 'Sonyere BridgeCare maka nlekọta ahụ ike dị mma',
    
    // Common UI
    confirm: 'Kwenye',
    pending: 'Na-eche',
    completed: 'Emechara',
    cancelled: 'Kagbuo',
    yes: 'Ee',
    download: 'Budata',
    overview: 'Nchịkọta',
    tips: 'Ndụmọdụ',
    viewAll: 'Lee niile',
    
    // Additional keys (using simplified Igbo and English for medical terms)
    upcomingAppointmentsTitle: 'Nzukọ Na-abịa',
    pastAppointmentsTitle: 'Nzukọ Gara Aga',
    noPastAppointments: 'Enweghị nzukọ gara aga',
    manageHealthcareAppointments: 'Jikwaa nzukọ ahụ ike gị',
    appointmentConfirmedSuccessfully: 'Ekwenyela nzukọ nke ọma!',
    refundProcessedIfApplicable: 'A ga-akwụghachi ego ma ọ bụrụ na ọ kwesịrị',
    doctorsNotes: 'Ndekọ Dọkịta',
    cancellationReason: 'Ihe Kpatara Nkagbu',
    searchConversations: 'Chọọ mkparịta ụka',
    noConversationsYet: 'Enwebeghị mkparịta ụka',
    doctorWillMessageYou: 'Dọkịta ga-ezitere gị ozi mgbe ha nwere mmelite',
    typeAMessage: 'Pịnye ozi',
    noConversationSelected: 'Ahọpụtaghị mkparịta ụka',
    selectConversationToStart: 'Họrọ mkparịta ụka iji malite iziga ozi',
    trackChildHealthSubtitle: 'Soro uto na mmepe nwa gị',
    immunizationScheduleTitle: 'Usoro Ọgwụ Mgbochi',
    growthChartTitle: 'Eserese Uto',
    developmentalMilestonesTitle: 'Isi Mmepe',
    healthTipsForAge: 'Ndụmọdụ Ahụ Ike Maka Afọ',
    provideBalancedMeals: 'Nye nri kwesịrị ekwesị nwere ihe na-edozi ahụ',
    hoursOfSleep: 'A na-atụ anya awa ụra 10-12 kwa ụbọchị',
    encouragePlay: 'Kwalite egwuregwu na mmụta',
    patientsTitle: 'Ndị Ọrịa',
    patientsSubtitle: 'Jikwaa ndekọ ndị ọrịa gị',
    patientList: 'Ndepụta Ndị Ọrịa',
    selectPatientToViewDetails: 'Họrọ onye ọrịa iji hụ nkọwa',
    healthTrackingTitle: 'Nchekwa Ahụ Ike',
    healthTrackingSubtitle: 'Lelee usoro ahụ ike ndị ọrịa',
    antenatalCareTracking: 'Nchekwa Nlekọta Afọ Ime',
    postnatalCareTracking: 'Nchekwa Nlekọta Mgbe Amụchara',
    manageMaternalHealth: 'Jikwaa ahụ ike nne na nlekọta ndụmọdụ',
    currentPregnancyStatus: 'Ọnọdụ Afọ Ime Ugbu a',
    lastCheckup: 'Nyocha Ikpeazụ',
    nextCheckup: 'Nyocha Na-esote',
    latestVitals: 'Ihe Dị Mkpa Ọhụrụ',
    latestNotes: 'Ndekọ Ọhụrụ',
    
    // Landing Page
    trustedMaternalChildHealthcare: 'Nlekọta Ahụ Ike Nne na Nwa A Tụkwasịrị Obi',
    healthcareThatBridgesGap: 'Nlekọta Ahụ Ike Na-Ejikọta Ọdịiche',
    professionalTelemedicine: 'Ọrụ telemedicine ọkachamara maka nne na ụmụaka na Naịjirịa. Nlekọta dị mma, mgbe ọ bụla, ebe ọ bụla.',
    getStartedFree: 'Malite N\'efu',
    signIn: 'Banye',
    patientsServed: 'Ndị Ọrịa E Nyerela Aka',
    healthcareProviders: 'Ndị Na-ahụ Maka Ahụ Ike',
    averageRating: 'Nkwụghachi Nkezi',
    everythingYouNeed: 'Ihe Niile Ị Chọrọ',
    comprehensiveMaternal: 'Nlekọta ahụ ike nne na nwa zuru oke n\'aka gị',
    videoConsultations: 'Nkwurịta Okwu Vidio',
    videoConsultationsDesc: 'Jikọọ gị na ndị dọkịta tozuru etozu site n\'ụlọ gị',
    maternalCare: 'Nlekọta Nne',
    maternalCareDesc: 'Nlekọta tupu na mgbe amụchara maka ndị nne na-atụ ime na ndị ọhụrụ',
    childHealthCare: 'Nlekọta Ahụ Ike Ụmụaka',
    childHealthDesc: 'Nkwurịta okwu ụmụaka, nchekwa ọgwụ mgbochi, na nchekwa uto',
    availability24_7: 'Ịdị Njikere 24/7',
    availability24_7Desc: 'Ịnweta ndị ọkachamara ahụ ike mgbe ọ bụla ị chọrọ ha',
    healthTrackingService: 'Nchekwa Ahụ Ike',
    healthTrackingDesc: 'Lelee njem ahụ ike gị na nchekwa na nghọta zuru ezu',
    securePrivate: 'Nchekwa & Nzuzo',
    securePrivateDesc: 'Ikpo okwu zuru oke na-eme ka data ahụ ike gị nọrọ na nzuzo na nchekwa',
    howItWorks: 'Otu Ọ Si Arụ Ọrụ',
    getStartedInSteps: 'Malite na nzọụkwụ 3 dị mfe',
    createYourAccount: 'Mepụta Akaụntụ Gị',
    createAccountDesc: 'Debanye aha n\'okpuru nkeji 2 na ozi ndabere gị. Enweghị akwụkwọ, enweghị nsogbu.',
    bookAppointment: 'Tinye Nhọpụta',
    bookAppointmentDesc: 'Họrọ ọrụ ị masịrị wee hazie nzụkọ dịka ọ dabara gị.',
    getQualityCare: 'Nweta Nlekọta Dị Mma',
    getQualityCareDesc: 'Jikọọ na ndị ọkachamara ahụ ike wee nweta nlekọta na ọgwụgwọ nkeonwe.',
    whatPatientsSay: 'Ihe Ndị Ọrịa Na-ekwu',
    realStoriesFromRealPeople: 'Akụkọ n\'ezie sitere n\'aka ndị mmadụ n\'ezie',
    testimonial1: '"BridgeCare mere ka njem afọ ime m dị mfe karịa. Ịkwurịta okwu na ndị dọkịta site n\'ụlọ bụ ngọzi!"',
    testimonial1Name: 'Aisha M.',
    testimonial1Title: 'Nne Ọhụrụ, Lagos',
    testimonial2: '"Nzaghachi ngwa ngwa, ndị dọkịta ọkachamara, na ọnụ ahịa dị ọnụ ala. BridgeCare bụ ihe Naịjirịa chọrọ!"',
    testimonial2Name: 'Chidinma O.',
    testimonial2Title: 'Nne/Nna, Abuja',
    testimonial3: '"Njirimara nchekwa ahụ ike nyeere m aka ịlele mmepe nwa m. Ana m akwado ya nye ndị nne na nna niile!"',
    testimonial3Name: 'Yusuf A.',
    testimonial3Title: 'Nna, Kano',
    readyToGetStarted: 'Ọ Dị Njikere Ịmalite?',
    joinThousands: 'Sonyere ọtụtụ puku ezinụlọ na-atụkwasị BridgeCare obi maka mkpa ahụ ike ha',
    createFreeAccount: 'Mepụta Akaụntụ N\'efu',
    
    // Doctor/Nurse Messages
    newChat: 'Mkparịta Ụka ỌHỤRỤ',
    startNewChat: 'Malite Mkparịta Ụka ỌHỤRỤ',
    clickNewChatToStart: 'Pịa "Mkparịta Ụka ỌHỤRỤ" iji malite iziga ozi onye ọrịa',
    backToPatients: 'Laghachi Na Ndị Ọrịa',
    
    // Schedule Appointment
    createNewAppointmentFor: 'Mepụta nzụkọ ọhụrụ maka',
    selectDate: 'Họrọ Ụbọchị',
    selectConsultationType: 'Họrọ Ụdị Nzụkọ',
    appointmentNotes: 'Ndekọ Nzụkọ',
    
    // Nurse Dashboard
    uploadVitalsFor: 'Bulite ihe dị mkpa maka',
    scheduleHomeVisit: 'Hazie nleta ụlọ',
    homeVisits: 'Nleta Ụlọ',
    uploadPatientVitalsTitle: 'Bulite Ihe Dị Mkpa Onye Ọrịa',
    
    // Common Dashboard
    thisWeek: 'Izu a',
    newPatients: 'Ndị Ọrịa ỌHỤRỤ',
    followUps: 'Ncheta',
    consultations: 'Nkwurịta Okwu',
    vitalsUploadedStat: 'Ihe Dị Mkpa E Bulitere',
    dueToday: 'Kwesịrị taa',
    tomorrow: 'Echi',
    upload: 'Bulite',
    schedule: 'Hazie',
    view: 'Lee',
    selectPatientToStartMessaging: 'Họrọ onye ọrịa iji malite iziga ozi',
    selectDoctorToStartMessaging: 'Họrọ dọkinta iji malite iziga ozi',
    
    // PatientRecord
    allergies: 'Ihe Na-akpata Ọrịa',
    medications: 'Ọgwụ',
    vitalsHistory: 'Akụkọ Ihe Dị Mkpa',
    consultationHistory: 'Akụkọ Nkwurịta Okwu',
    
    // AddPatient
    searchForPatientAndAdd: 'Chọọ onye ọrịa wee tinye nkọwa ahụ ike ha',
    vitalsUploadedSuccessfully: 'E Bulitere Ihe Dị Mkpa Nke Ọma',
    followUpWith: 'Soro',
    patientUpdates: 'Mmelite onye ọrịa',
    
    // Sidebar
    language: 'Asụsụ',
    
    // AddPatient Page
    addMedicalRecords: 'Tinye Ndekọ Ahụ Ike',
    searchPatient: 'Chọọ Onye Ọrịa',
    searchByNameEmailPhone: 'Chọọ site na aha, email, ma ọ bụ ekwentị...',
    medicalRecordsAddedSuccessfully: 'E tinyere ndekọ ahụ ike nke ọma!',
    
    // UploadVitals Page
    bloodPressure: 'Nghọta Ọbara',
    systolic: 'Systolic',
    diastolic: 'Diastolic',
    pulseRate: 'Ọsọ Pulse',
    guidelines: 'Nduzi',
    normalRanges: 'Nha Nkịtị',
    
    // Additional Doctor/Nurse Dashboard Keys
    todaysAppointments: 'Nhọta Taa',
    welcomeBackDoctor: 'Nnọọ ọzọ, Dọkịta Amina Yusuf',
    welcomeBackNurse: 'Nnọọ ọzọ',
    recentAlerts: 'Ọkwa Ọhụrụ',
    searchPatients: 'Chọọ ndị ọrịa...',
    searchDoctors: 'Chọọ ndị dọkịta...',
    
    // Appointment Types
    antenatalCheckup: 'Nlele Mgbe Dị Ime',
    postnatalFollowup: 'Nleta Mgbe Amụchara',
    pregnancyConsultation: 'Ndụmọdụ Afọ Ime',
    childHealthReview: 'Nlele Ahụ Ike Nwata',
    
    // Status & Mode (only new ones)
    recovering: 'Na-agbake',
    healthy: 'Ahụ Ike',
    
    // Table Headers (only new ones)
    week: 'Izu',
    bp: 'BP',
    weightKg: 'Ịdị arọ (kg)',
    fundalHeightCm: 'Ịdị elu Fundal (cm)',
    weeksPostpartum: 'Izu Mgbe Amụchara',
    breastfeeding: 'Inye Ara',
    childParent: 'Nwata (Nne/Nna)',
    
    // Form Labels (only new ones)
    weightKgLabel: 'Ịdị arọ (kg)',
    heightCmLabel: 'Ịdị elu (cm)',
    bloodPressureMmhg: 'Nghọta Ọbara (mmHg)',
    temperatureC: 'Okpomọkụ (°C)',
    heartRateBpm: 'Ọsọ Obi (bpm)',
    pregnancyStatus: 'Ọnọdụ Afọ Ime',
    gestationalAgeWeeks: 'Afọ Ime (izu)',
    lastMenstrualPeriod: 'Oge Nsọ Ikpeazụ',
    
    // UploadVitals (only new ones)
    recordAndUpload: 'Dee ma bulite ihe ngosi ndụ onye ọrịa',
    selectPatientStar: 'Họrọ Onye Ọrịa *',
    notesOptional: 'Ndetu (Nhọrọ)',
    ensureAccurateMeasurements: 'Jide n\'aka na ntụ niile ziri ezi',
    useCalibratedEquipment: 'Jiri ngwa ọrụ ahaziri ahazi',
    recordPatientPosition: 'Dee ọnọdụ onye ọrịa mgbe ị na-atụ',
    noteUnusualObservations: 'Chọpụta ihe ọ bụla pụrụ iche ozugbo',
    greatWork: 'Ọrụ Dị Egwu!',
  },
  ha: {
    // Hausa translations
    welcome: 'Barka da zuwa',
    welcomeBack: 'Barka da dawowa',
    login: 'Shiga',
    signup: 'Yi Rajista',
    logout: 'Fita',
    email: 'Imel',
    password: 'Kalmar sirri',
    name: 'Suna',
    fullName: 'Cikakken suna',
    role: 'Matsayi',
    submit: 'Tura',
    cancel: 'Soke',
    saveButton: 'Ajiye',
    delete: 'Goge',
    edit: 'Gyara',
    search: 'Nema',
    
    // Roles
    patient: 'Mai haƙuri',
    doctor: 'Likita',
    nurse: 'Nas / CHW',
    
    // Navigation
    dashboard: 'Dashboard',
    appointments: 'Alƙawura',
    payments: 'Biyan kuɗi',
    profile: 'Bayani',
    settings: 'Saiti',
    messages: 'Saƙonni',
    
    // Services
    bookConsultation: 'Yi Alƙawari',
    maternalHealth: 'Lafiyar Uwa',
    childHealth: 'Lafiyar Yara',
    healthRecords: 'Bayanan lafiya',
    uploadVitalsButton: 'Loda alamomi',
    
    // Dashboard
    welcomeBackExclaim: 'Barka da dawowa!',
    doctorDashboard: 'Dashboard na Likita',
    nurseDashboard: 'Dashboard na Nas',
    hereDashboardOverview: 'Ga taƙaitaccen bayanin lafiyar ku',
    
    // Stats
    subscriptionStatus: 'Matsayin rajista',
    active: 'Mai aiki',
    inactive: 'Ba mai aiki ba',
    totalPatients: 'Jimlar marasa lafiya',
    totalRecords: 'Jimlar bayanan',
    nextAppointment: 'Alƙawari na gaba',
    
    // Hero/Landing
    heroTitle: 'Kiwon Lafiyar Uwa da Yara Mai Inganci, Daga Ko\'ina',
    heroSubtitle: 'Haɗu da ƙwararrun ma\'aikatan kiwon lafiya don kulawa kafin haihuwa, bayan haihuwa, da lafiyar yara',
    
    // Login/Signup
    welcomeBackTitle: 'Barka da dawowa',
    signInToContinue: 'Shiga don ci gaba zuwa BridgeCare',
    createAccountTitle: 'Ƙirƙiri Asusu',
    joinBridgeCare: 'Shiga BridgeCare don samun kulawa mai inganci',
    
    // Common UI
    confirm: 'Tabbatar',
    pending: 'Ana jira',
    completed: 'An kammala',
    cancelled: 'An soke',
    yes: 'Eh',
    download: 'Sauke',
    overview: 'Taƙaitaccen bayani',
    tips: 'Shawarwari',
    viewAll: 'Duba duka',
    
    // Additional keys (using simplified Hausa and English for medical terms)
    upcomingAppointmentsTitle: 'Alƙawura Masu Zuwa',
    pastAppointmentsTitle: 'Alƙawura Da Suka Wuce',
    noPastAppointments: 'Babu alƙawura da suka wuce',
    manageHealthcareAppointments: 'Sarrafa alƙawuran kula da lafiya',
    appointmentConfirmedSuccessfully: 'An tabbatar da alƙawari cikin nasara!',
    refundProcessedIfApplicable: 'Za a mayar da kuɗi idan ya dace',
    doctorsNotes: 'Bayanin Likita',
    cancellationReason: 'Dalilin Soke',
    searchConversations: 'Nema tattaunawa',
    noConversationsYet: 'Babu tattaunawa har yanzu',
    doctorWillMessageYou: 'Likita zai aika muku saƙon lokacin da suke da sabuntawa',
    typeAMessage: 'Rubuta saƙo',
    noConversationSelected: 'Ba a zaɓi tattaunawa ba',
    selectConversationToStart: 'Zaɓi tattaunawa don fara saƙon',
    trackChildHealthSubtitle: 'Bi ci gaban yaro da haɓakansa',
    immunizationScheduleTitle: 'Jadawalin Rigakafi',
    growthChartTitle: 'Ginshiƙin Girma',
    developmentalMilestonesTitle: 'Matsayin Ci gaba',
    healthTipsForAge: 'Shawarwarin Lafiya don Shekaru',
    provideBalancedMeals: 'Samar da abinci mai ma\'auni tare da abubuwan gina jiki',
    hoursOfSleep: 'Ana ba da shawarar sa\'o\'i 10-12 na barci kowace rana',
    encouragePlay: 'Ƙarfafa wasa da koyo',
    patientsTitle: 'Marasa lafiya',
    patientsSubtitle: 'Sarrafa bayanan marasa lafiya',
    patientList: 'Jerin Marasa Lafiya',
    selectPatientToViewDetails: 'Zaɓi mai haƙuri don ganin cikakken bayani',
    healthTrackingTitle: 'Bin Lafiya',
    healthTrackingSubtitle: 'Lura da ma\'aunin lafiyar marasa lafiya',
    antenatalCareTracking: 'Bin Kulawar Ciki',
    postnatalCareTracking: 'Bin Kulawar Bayan Haihuwa',
    manageMaternalHealth: 'Sarrafa lafiyar uwa da samun shawarwari',
    currentPregnancyStatus: 'Matsayin Ciki na Yanzu',
    lastCheckup: 'Jarrabawar ƙarshe',
    nextCheckup: 'Jarrabawa ta Gaba',
    latestVitals: 'Sabbin Alamomi',
    latestNotes: 'Sabbin Bayanai',
    
    // Landing Page
    trustedMaternalChildHealthcare: 'Kulawar Lafiyar Uwa da Yara Mai Aminci',
    healthcareThatBridgesGap: 'Kulawar Lafiya Mai Haɗa Tazara',
    professionalTelemedicine: 'Ayyukan telemedicine na ƙwararru don iyaye mata da yara a Najeriya. Kulawa mai kyau, kowane lokaci, ko\'ina.',
    getStartedFree: 'Fara Kyauta',
    signIn: 'Shiga',
    patientsServed: 'Marasa Lafiya Da Aka Yi Wa Hidima',
    healthcareProviders: 'Masu Ba Da Kulawar Lafiya',
    averageRating: 'Matsakaicin Kimanta',
    everythingYouNeed: 'Duk Abin Da Kake Buƙata',
    comprehensiveMaternal: 'Cikakken kulawar lafiyar uwa da yara a hannun ka',
    videoConsultations: 'Shawarwarin Bidiyo',
    videoConsultationsDesc: 'Haɗu da ƙwararrun likitoci daga gidanku',
    maternalCare: 'Kulawar Uwa',
    maternalCareDesc: 'Kulawar kafin haihuwa da bayan haihuwa don iyaye mata masu juna biyu da sababbi',
    childHealthCare: 'Kulawar Lafiyar Yara',
    childHealthDesc: 'Shawarwarin yara, bin diddigin rigakafin, da bin ci gaban girma',
    availability24_7: 'Samun Dama 24/7',
    availability24_7Desc: 'Samun damar yin magana da ƙwararrun lafiya duk lokacin da kuke buƙata su',
    healthTrackingService: 'Bin Lafiya',
    healthTrackingDesc: 'Lura da tafiyar lafiyar ku tare da cikakken bin diddigin da fahimta',
    securePrivate: 'Tsaro & Sirri',
    securePrivateDesc: 'Dandamali mai tsaro yana tabbatar da cewa bayanan lafiyar ku sun kasance masu zaman kansu kuma masu aminci',
    howItWorks: 'Yadda Yake Aiki',
    getStartedInSteps: 'Fara cikin matakai 3 masu sauƙi',
    createYourAccount: 'Ƙirƙiri Asusun Ku',
    createAccountDesc: 'Yi rajista a cikin mintuna 2 tare da bayanan ku na asali. Babu takarda, babu matsala.',
    bookAppointment: 'Yi Ajiya',
    bookAppointmentDesc: 'Zaɓi sabis ɗin da kuka fi so kuma tsara shawarwari kamar yadda ya dace da ku.',
    getQualityCare: 'Sami Kulawa Mai Kyau',
    getQualityCareDesc: 'Haɗu da ƙwararrun kiwon lafiya kuma ku sami kulawa da magani na musamman.',
    whatPatientsSay: 'Abin Da Marasa Lafiya Ke Faɗa',
    realStoriesFromRealPeople: 'Labarun gaskiya daga mutane na gaske',
    testimonial1: '"BridgeCare ya sauƙaƙa tafiyar cikina sosai. Yin magana da likitoci daga gida ya kasance albarka!"',
    testimonial1Name: 'Aisha M.',
    testimonial1Title: 'Sabuwar Uwa, Lagos',
    testimonial2: '"Saurin amsoshi, ƙwararrun likitoci, da farashin araha. BridgeCare shine abin da Najeriya ke buƙata!"',
    testimonial2Name: 'Chidinma O.',
    testimonial2Title: 'Iyaye, Abuja',
    testimonial3: '"Abubuwan bin diddigin lafiya sun taimake ni wajen lura da ci gaban jariri na. Ina ba da shawarwari ga duk iyaye!"',
    testimonial3Name: 'Yusuf A.',
    testimonial3Title: 'Uba, Kano',
    readyToGetStarted: 'Shin Kuna Shirye Don Farawa?',
    joinThousands: 'Kasance tare da dubban iyalai da ke dogara ga BridgeCare don buƙatun lafiya',
    createFreeAccount: 'Ƙirƙiri Asusun Kyauta',
    
    // Doctor/Nurse Messages
    newChat: 'Sabon Hira',
    startNewChat: 'Fara Sabon Hira',
    clickNewChatToStart: 'Danna "Sabon Hira" don fara aika saƙon mai haƙuri',
    backToPatients: 'Koma Zuwa Marasa Lafiya',
    
    // Schedule Appointment
    scheduleAppointment: 'Yi Alƙawari',
    createNewAppointmentFor: 'Yi sabon alƙawari don',
    selectDate: 'Zaɓi Kwanan Wata',
    selectConsultationType: 'Zaɓi Irin Shawarwari',
    appointmentNotes: 'Bayanan Alƙawari',
    
    // Nurse Dashboard
    uploadVitalsFor: 'Loda alamomi don',
    scheduleHomeVisit: 'Yi jadawalin ziyarar gida',
    homeVisits: 'Ziyarar Gida',
    uploadPatientVitalsTitle: 'Loda Alamomin Marasa Lafiya',
    
    // Common Dashboard
    thisWeek: 'Wannan Mako',
    newPatients: 'Sabbin Marasa Lafiya',
    followUps: 'Bin Diddigi',
    consultations: 'Shawarwari',
    vitalsUploadedStat: 'An Loda Alamomi',
    dueToday: 'Yau ya kamata',
    tomorrow: 'Gobe',
    upload: 'Loda',
    schedule: 'Tsara',
    view: 'Duba',
    selectPatientToStartMessaging: 'Zaɓi mai haķuri don fara aika saķo',
    selectDoctorToStartMessaging: 'Zaɓi likita don fara aika saķo',
    
    // PatientRecord
    allergies: 'Abubuwan Da Ke Haifar Da Rashin Lafiya',
    medications: 'Magunguna',
    vitalsHistory: 'Tarihin Alamomi',
    consultationHistory: 'Tarihin Shawarwari',
    
    // AddPatient
    searchForPatientAndAdd: 'Nemo mai haķuri kuma ķara bayanan lafiyarsa',
    vitalsUploadedSuccessfully: 'An Loda Alamomi Cikin Nasara',
    followUpWith: 'Bi diddigin tare da',
    patientUpdates: 'Sabuntaķe marasa lafiya',
    
    // Sidebar
    language: 'Harshe',
    
    // AddPatient Page
    addMedicalRecords: 'Ķara Bayanan Likita',
    searchPatient: 'Nemo Mai Haķuri',
    searchByNameEmailPhone: 'Nemo ta suna, email, ko waya...',
    medicalRecordsAddedSuccessfully: 'An ķara bayanan likita cikin nasara!',
    
    // UploadVitals Page
    bloodPressure: 'Matsanancin Jini',
    systolic: 'Systolic',
    diastolic: 'Diastolic',
    pulseRate: 'Guķun Pulse',
    guidelines: 'Jagororin',
    normalRanges: 'Maķaunin Alʼada',
    
    // Additional Doctor/Nurse Dashboard Keys
    todaysAppointments: 'Alƙawuran Yau',
    welcomeBackDoctor: 'Barka da zuwa, Likita Amina Yusuf',
    welcomeBackNurse: 'Barka da zuwa',
    recentAlerts: 'Sanarwa Na Kwanan Nan',
    searchPatients: 'Nemo marasa lafiya...',
    searchDoctors: 'Nemo likitoci...',
    
    // Appointment Types
    antenatalCheckup: 'Dubawa Kafin Haihuwa',
    postnatalFollowup: 'Bincike Bayan Haihuwa',
    pregnancyConsultation: 'Shawarwari Ciki',
    childHealthReview: 'Bitar Lafiyar Yaro',
    
    // Status & Mode (only new ones)
    recovering: 'Yana Warkewa',
    healthy: 'Lafiya',
    
    // Table Headers (only new ones)
    week: 'Mako',
    bp: 'BP',
    weightKg: 'Nauyi (kg)',
    fundalHeightCm: 'Tsayin Fundal (cm)',
    weeksPostpartum: 'Makonni Bayan Haihuwa',
    breastfeeding: 'Shayarwa',
    childParent: 'Yaro (Iyaye)',
    
    // Form Labels (only new ones)
    weightKgLabel: 'Nauyi (kg)',
    heightCmLabel: 'Tsawo (cm)',
    bloodPressureMmhg: 'Matsanancin Jini (mmHg)',
    temperatureC: 'Zafin Jiki (°C)',
    heartRateBpm: 'Bugun Zuciya (bpm)',
    pregnancyStatus: 'Matsayin Ciki',
    gestationalAgeWeeks: 'Lokacin Ciki (makonni)',
    lastMenstrualPeriod: 'Haila Ta Ƙarshe',
    
    // UploadVitals (only new ones)
    recordAndUpload: 'Yi rikodin kuma ɗora alamun rai na mai haƙuri',
    selectPatientStar: 'Zaɓi Mai Haƙuri *',
    notesOptional: 'Bayanai (Zaɓi)',
    ensureAccurateMeasurements: 'Tabbatar duk aunawa daidai suke',
    useCalibratedEquipment: 'Yi amfani da kayan aiki da aka daidaita',
    recordPatientPosition: 'Yi rikodin matsayin mai haƙuri lokacin aunawa',
    noteUnusualObservations: 'Lura da duk wani abu na ban mamaki nan take',
    greatWork: 'Aiki Mai Kyau!',
  },
  pcm: {
    // Nigerian Pidgin translations
    welcome: 'Welcome',
    welcomeBack: 'Welcome back',
    login: 'Login',
    signup: 'Sign Up',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    fullName: 'Full name',
    role: 'Your Work',
    submit: 'Send am',
    cancel: 'Cancel',
    saveButton: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Find',
    
    // Roles
    patient: 'Sick Pesin',
    doctor: 'Dokita',
    nurse: 'Nurse / CHW',
    
    // Navigation
    dashboard: 'Dashboard',
    appointments: 'Appointments',
    payments: 'Payment',
    profile: 'Your Info',
    settings: 'Settings',
    messages: 'Messages',
    
    // Services
    bookConsultation: 'Book Meeting',
    maternalHealth: 'Mama Health',
    childHealth: 'Pikin Health',
    healthRecords: 'Health Records',
    uploadVitalsButton: 'Upload Body Signs',
    
    // Dashboard
    welcomeBackExclaim: 'Welcome back!',
    doctorDashboard: 'Dokita Dashboard',
    nurseDashboard: 'Nurse Dashboard',
    hereDashboardOverview: 'Na your health overview be this',
    
    // Stats
    subscriptionStatus: 'Subscription Status',
    active: 'Dey Work',
    inactive: 'No Dey Work',
    totalPatients: 'All Sick Pesin',
    totalRecords: 'All Records',
    nextAppointment: 'Next Appointment',
    
    // Hero/Landing
    heroTitle: 'Quality Mama & Pikin Health Care, From Anywhere',
    heroSubtitle: 'Connect with proper health workers for mama wey dey belle, after born, and pikin health',
    
    // Login/Signup
    welcomeBackTitle: 'Welcome Back',
    signInToContinue: 'Login to continue for BridgeCare',
    createAccountTitle: 'Create Account',
    joinBridgeCare: 'Join BridgeCare for quality health care',
    
    // Common UI
    confirm: 'Confirm',
    pending: 'Dey wait',
    completed: 'Don finish',
    cancelled: 'Cancel am',
    yes: 'Yes',
    download: 'Download',
    overview: 'Summary',
    tips: 'Tips',
    viewAll: 'See all',
    
    // Additional keys (using Pidgin English)
    upcomingAppointmentsTitle: 'Appointments Wey Dey Come',
    pastAppointmentsTitle: 'Old Appointments',
    noPastAppointments: 'No old appointments',
    manageHealthcareAppointments: 'Manage your health appointments',
    appointmentConfirmedSuccessfully: 'Appointment don confirm well well!',
    refundProcessedIfApplicable: 'We go return your money if e fit',
    doctorsNotes: 'Dokita Notes',
    cancellationReason: 'Why You Cancel Am',
    searchConversations: 'Find conversations',
    noConversationsYet: 'No conversations yet',
    doctorWillMessageYou: 'Dokita go message you when e get update',
    typeAMessage: 'Type message',
    noConversationSelected: 'You never pick conversation',
    selectConversationToStart: 'Pick conversation make you start messaging',
    trackChildHealthSubtitle: 'Follow your pikin growth and development',
    immunizationScheduleTitle: 'Vaccine Schedule',
    growthChartTitle: 'Growth Chart',
    developmentalMilestonesTitle: 'Development Progress',
    healthTipsForAge: 'Health Tips for Age',
    provideBalancedMeals: 'Give balanced food with good nutrients',
    hoursOfSleep: 'Pikin need 10-12 hours sleep everyday',
    encouragePlay: 'Make pikin play and learn',
    patientsTitle: 'Sick People',
    patientsSubtitle: 'Manage patient records',
    patientList: 'Patient List',
    selectPatientToViewDetails: 'Pick patient make you see details',
    healthTrackingTitle: 'Health Tracking',
    healthTrackingSubtitle: 'Monitor patient health',
    antenatalCareTracking: 'Belle Care Tracking',
    postnatalCareTracking: 'After Birth Care Tracking',
    manageMaternalHealth: 'Manage mama health and get advice',
    currentPregnancyStatus: 'Current Belle Status',
    lastCheckup: 'Last Checkup',
    nextCheckup: 'Next Checkup',
    latestVitals: 'Latest Body Signs',
    latestNotes: 'Latest Notes',
    
    // Landing Page
    trustedMaternalChildHealthcare: 'Mama and Pikin Health Care Wey We Trust',
    healthcareThatBridgesGap: 'Health Care Wey Dey Connect People',
    professionalTelemedicine: 'Professional telemedicine service for mama and pikin for Naija. Good care, anytime, anywhere.',
    getStartedFree: 'Start Free',
    signIn: 'Sign In',
    patientsServed: 'Sick People Wey We Don Serve',
    healthcareProviders: 'Health Workers',
    averageRating: 'Average Rating',
    everythingYouNeed: 'Everything Wey You Need',
    comprehensiveMaternal: 'Complete mama and pikin health care for your hand',
    videoConsultations: 'Video Meetings',
    videoConsultationsDesc: 'Talk to qualified doctors from your house',
    maternalCare: 'Mama Care',
    maternalCareDesc: 'Before birth and after birth care for belle women and new mamas',
    childHealthCare: 'Pikin Health Care',
    childHealthDesc: 'Pikin consultations, injection tracking, and growth monitoring',
    availability24_7: '24/7 Dey Available',
    availability24_7Desc: 'You fit talk to health workers anytime wey you need dem',
    healthTrackingService: 'Health Tracking',
    healthTrackingDesc: 'Monitor your health journey with complete tracking and info',
    securePrivate: 'Safe & Private',
    securePrivateDesc: 'Platform wey dey keep your health data private and safe',
    howItWorks: 'How E Dey Work',
    getStartedInSteps: 'Start with 3 simple steps',
    createYourAccount: 'Create Your Account',
    createAccountDesc: 'Sign up in under 2 minutes with your basic info. No paperwork, no wahala.',
    bookAppointment: 'Book Appointment',
    bookAppointmentDesc: 'Choose the service wey you like and schedule consultation when e fit you.',
    getQualityCare: 'Get Quality Care',
    getQualityCareDesc: 'Connect with health workers and receive personal care and treatment.',
    whatPatientsSay: 'Wetin Patients Dey Talk',
    realStoriesFromRealPeople: 'Real stories from real people',
    testimonial1: '"BridgeCare make my belle journey too easy. To talk with doctors from house na blessing!"',
    testimonial1Name: 'Aisha M.',
    testimonial1Title: 'New Mama, Lagos',
    testimonial2: '"Quick responses, professional doctors, and cheap prices. BridgeCare na wetin Naija need!"',
    testimonial2Name: 'Chidinma O.',
    testimonial2Title: 'Parent, Abuja',
    testimonial3: '"The health tracking features help me monitor my baby development. I recommend am for all parents!"',
    testimonial3Name: 'Yusuf A.',
    testimonial3Title: 'Papa, Kano',
    readyToGetStarted: 'You Don Ready To Start?',
    joinThousands: 'Join thousands of families wey dey trust BridgeCare for their health needs',
    createFreeAccount: 'Create Free Account',
    
    // Doctor/Nurse Messages
    newChat: 'New Chat',
    startNewChat: 'Start New Chat',
    clickNewChatToStart: 'Click "New Chat" make you start to message patient',
    backToPatients: 'Go Back To Patients',
    
    // Schedule Appointment
    createNewAppointmentFor: 'Create new appointment for',
    selectDate: 'Pick Date',
    selectConsultationType: 'Pick Consultation Type',
    appointmentNotes: 'Appointment Notes',
    
    // Nurse Dashboard
    uploadVitalsFor: 'Upload vitals for',
    scheduleHomeVisit: 'Schedule house visit',
    homeVisits: 'House Visits',
    uploadPatientVitalsTitle: 'Upload Patient Vitals',
    
    // Common Dashboard
    thisWeek: 'This Week',
    newPatients: 'New Patients',
    followUps: 'Follow-ups',
    consultations: 'Consultations',
    vitalsUploadedStat: 'Vitals Wey Don Upload',
    dueToday: 'Suppose don finish today',
    tomorrow: 'Tomorrow',
    upload: 'Upload',
    schedule: 'Schedule',
    view: 'View',
    selectPatientToStartMessaging: 'Pick patient make you start to message',
    selectDoctorToStartMessaging: 'Pick doctor make you start to message',
    
    // PatientRecord
    allergies: 'Things Wey Dey Cause Allergy',
    medications: 'Drugs',
    vitalsHistory: 'Vitals History',
    consultationHistory: 'Consultation History',
    
    // AddPatient
    searchForPatientAndAdd: 'Find patient and add their medical details',
    vitalsUploadedSuccessfully: 'Vitals Don Upload Successfully',
    followUpWith: 'Follow-up with',
    patientUpdates: 'Patient updates',
    
    // Sidebar
    language: 'Language',
    
    // AddPatient Page
    addMedicalRecords: 'Add Medical Records',
    searchPatient: 'Find Patient',
    searchByNameEmailPhone: 'Find by name, email, or phone...',
    medicalRecordsAddedSuccessfully: 'Medical records don add successfully!',
    
    // UploadVitals Page
    bloodPressure: 'Blood Pressure',
    systolic: 'Systolic',
    diastolic: 'Diastolic',
    pulseRate: 'Pulse Rate',
    guidelines: 'Guidelines',
    normalRanges: 'Normal Ranges',
    
    // Additional Doctor/Nurse Dashboard Keys
    todaysAppointments: 'Today Appointments',
    welcomeBackDoctor: 'Welcome back, Dr. Amina Yusuf',
    welcomeBackNurse: 'Welcome back',
    recentAlerts: 'New Alerts',
    searchPatients: 'Find sick pesin...',
    searchDoctors: 'Find doctors...',
    
    // Appointment Types
    antenatalCheckup: 'Check Before Born Pikin',
    postnatalFollowup: 'Check After Born Pikin',
    pregnancyConsultation: 'Belle Advice',
    childHealthReview: 'Pikin Health Check',
    
    // Status & Mode (only new ones)
    recovering: 'E Dey Get Better',
    healthy: 'Healthy',
    
    // Table Headers (only new ones)
    week: 'Week',
    bp: 'BP',
    weightKg: 'Weight (kg)',
    fundalHeightCm: 'Fundal Height (cm)',
    weeksPostpartum: 'Weeks After Born Pikin',
    breastfeeding: 'Breastfeeding',
    childParent: 'Pikin (Papa/Mama)',
    
    // Form Labels (only new ones)
    weightKgLabel: 'Weight (kg)',
    heightCmLabel: 'Height (cm)',
    bloodPressureMmhg: 'Blood Pressure (mmHg)',
    temperatureC: 'Temperature (°C)',
    heartRateBpm: 'Heart Rate (bpm)',
    pregnancyStatus: 'Belle Status',
    gestationalAgeWeeks: 'Belle Age (weeks)',
    lastMenstrualPeriod: 'Last Period',
    
    // UploadVitals (only new ones)
    recordAndUpload: 'Write and upload sick pesin body signs',
    selectPatientStar: 'Choose Sick Pesin *',
    notesOptional: 'Notes (If You Want)',
    ensureAccurateMeasurements: 'Make sure say all measurements correct',
    useCalibratedEquipment: 'Use equipment wey dem don set well',
    recordPatientPosition: 'Write how sick pesin siddon when dem dey measure',
    noteUnusualObservations: 'Write anything wey no normal sharp sharp',
    greatWork: 'Great Work!',
  },
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const storedLanguage = localStorage.getItem('bridgecare_language');
    if (storedLanguage && translations[storedLanguage]) {
      setLanguage(storedLanguage);
    }
  }, []);

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
      localStorage.setItem('bridgecare_language', lang);
    }
  };

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English' },
      { code: 'yo', name: 'Yoruba' },
      { code: 'ig', name: 'Igbo' },
      { code: 'ha', name: 'Hausa' },
      { code: 'pcm', name: 'Pidgin' },
    ],
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
